export function createMap(mapEl, options = {}) {
  if (!window.L) {
    throw new Error("Leaflet failed to load.");
  }

  const map = L.map(mapEl, {
    zoomControl: false,
    preferCanvas: true,
    scrollWheelZoom: false,
  }).setView([45.8326, 6.8652], 12);

  const baseMaps = {};
  if (options.thunderforestUrl) {
    baseMaps.Terrain = L.tileLayer(options.thunderforestUrl, {
      maxZoom: 24,
      maxNativeZoom: 22,
      crossOrigin: "anonymous",
      attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | Map style: &copy; <a href="https://www.thunderforest.com/">Thunderforest</a>',
    });
  }

  Object.assign(baseMaps, {
    Topo: L.tileLayer("https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png", {
      maxZoom: 20,
      crossOrigin: "anonymous",
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, CyclOSM',
    }),
    "OSM Standard": L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      crossOrigin: "anonymous",
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }),
    "Satellite (ESRI)": L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      attribution: "Tiles &copy; Esri",
    }),
    "Topo (ESRI)": L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      attribution: "Tiles &copy; Esri",
    }),
  });

  (baseMaps.Terrain || baseMaps.Topo).addTo(map);
  addSearchControl(map);
  enableCtrlWheelZoom(map);
  L.control.zoom({ position: "topleft" }).addTo(map);
  L.control.layers(baseMaps, null, { collapsed: false, position: "topright" }).addTo(map);
  L.control.scale({ metric: true, imperial: true, position: "bottomleft" }).addTo(map);

  if (map.pm) {
    map.pm.setGlobalOptions({ snappable: false });
  }

  setTimeout(() => map.invalidateSize(), 50);
  window.addEventListener("resize", () => map.invalidateSize());

  return map;
}

function enableCtrlWheelZoom(map) {
  const container = map.getContainer();
  const hint = L.DomUtil.create("div", "bhgpxrp__scrollHint", container);
  let hintTimeout;

  container.addEventListener("wheel", (event) => {
    if (!event.ctrlKey) {
      hint.textContent = "Use Ctrl + scroll to zoom the map";
      hint.classList.add("is-visible");
      window.clearTimeout(hintTimeout);
      hintTimeout = window.setTimeout(() => hint.classList.remove("is-visible"), 1400);
      return;
    }

    hint.classList.remove("is-visible");
    event.preventDefault();
    const nextZoom = map.getZoom() + (event.deltaY < 0 ? 1 : -1);
    map.setZoomAround(map.mouseEventToLatLng(event), nextZoom);
  }, { passive: false });
}

function addSearchControl(map) {
  const SearchControl = L.Control.extend({
    options: {
      position: "topleft",
    },

    onAdd() {
      const container = L.DomUtil.create("div", "leaflet-control bhgpxrp__searchControl");
      container.innerHTML = `
        <form class="bhgpxrp__searchForm" role="search">
          <input class="bhgpxrp__searchInput" type="text" placeholder="Search places" aria-label="Search places" autocomplete="off" />
          <button class="bhgpxrp__searchClose" type="button" aria-label="Close search results">&times;</button>
          <button class="bhgpxrp__searchButton" type="submit">Search</button>
          <div class="bhgpxrp__searchStatus" aria-live="polite"></div>
          <div class="bhgpxrp__searchResults" role="listbox"></div>
        </form>
      `;

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      ["click", "dblclick", "mousedown", "mouseup", "pointerdown", "pointerup", "touchstart", "touchend"].forEach((eventName) => {
        container.addEventListener(eventName, (event) => event.stopPropagation());
      });

      const form = container.querySelector(".bhgpxrp__searchForm");
      const input = container.querySelector(".bhgpxrp__searchInput");
      const closeButton = container.querySelector(".bhgpxrp__searchClose");
      const status = container.querySelector(".bhgpxrp__searchStatus");
      const results = container.querySelector(".bhgpxrp__searchResults");

      closeButton.addEventListener("click", () => closeSearch(input, status, results));
      input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          closeSearch(input, status, results);
        }
      });

      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const query = input.value.trim();
        if (!query) {
          status.textContent = "Enter a place name.";
          results.innerHTML = "";
          return;
        }

        status.textContent = "Searching...";
        results.innerHTML = "";
        try {
          const places = await searchPlaces(query);
          if (!places.length) {
            status.textContent = "No places found.";
            return;
          }

          status.textContent = "";
          results.innerHTML = places.map((place, index) => `
            <button class="bhgpxrp__searchResult" type="button" role="option" data-result-index="${index}">
              ${escapeHtml(place.display_name || "Unnamed place")}
            </button>
          `).join("");

          results.querySelectorAll(".bhgpxrp__searchResult").forEach((button) => {
            button.addEventListener("click", () => {
              const place = places[Number(button.dataset.resultIndex)];
              map.fire("bhgpxrp:searchselected");
              moveToSearchResult(map, place);
              input.value = place.display_name || query;
              closeSearch(null, status, results);
            });
          });
        } catch (error) {
          status.textContent = "Search failed.";
          console.error(error);
        }
      });

      return container;
    },
  });

  map.addControl(new SearchControl());
}

async function searchPlaces(query) {
  const params = new URLSearchParams({
    format: "jsonv2",
    limit: "5",
    q: query,
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
    headers: {
      "Accept": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Place search failed (${response.status}).`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

function closeSearch(input, status, results) {
  if (input) {
    input.value = "";
  }
  status.textContent = "";
  results.innerHTML = "";
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function moveToSearchResult(map, result) {
  if (Array.isArray(result.boundingbox) && result.boundingbox.length === 4) {
    map.fitBounds([
      [Number(result.boundingbox[0]), Number(result.boundingbox[2])],
      [Number(result.boundingbox[1]), Number(result.boundingbox[3])],
    ], { maxZoom: 14 });
    return;
  }

  const latitude = Number(result.lat);
  const longitude = Number(result.lon);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    map.setView([latitude, longitude], 14);
  }
}
